import { Navigate, Route, Routes } from "react-router-dom";
// The RENDER-TIME half of the kiosk one-way door — browser back, cold boot and deep links,
// none of which reach history.js's push/replace guard. See its docstring.
import KioskLock from "./commonComponents/KioskLock";
import { KIOSK_ENTRY_ENABLED } from "@rista/core/kiosk/featureFlag";
import { Fragment, Suspense, use, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  ANALYTICS_ROUTE,
  ACCESS_ROUTE,
  INVENTORY_ROUTE,
  REPORTS_ROUTE,
  CATALOG_ROUTE,
  CUSTOMER_ROUTE,
  MARKETING_ROUTE,
  SUPPORT_ROUTE,
  MANAGE_TABLE_ROUTE,
  CASH_MANAGEMENT_ROUTE,
  CASHIER_SETTLEMENT_ROUTE,
  SUPPORT_LOGIN_ROUTE,
  OUTLET_STATUS_ROUTE,
  REDIRECT_PAGE_ROUTE,
  DISCOUNT_DEALS_ROUTE,
  CHECKOUT_CONTAINER_ROUTE,
  CUSTOMER_INFO_ROUTE,
  ITEM_STATUS_ROUTE,
  DETAILS_VIEW_ROUTE,
  ROUTE_CONSTANTS_MAP,
  LOGIN_ROUTE,
  ACCOUNT_ROUTE,
  RISTA_CARDS_ROUTE,
  COMPANIES_ROUTE,
  CUSTOMER_DISPLAY_SETTINGS_ROUTE
} from "utils/routeConstants";
import { RISTA_IFRAME_MODULE_IDS, MERCHANT_PANEL_PAGE_IDS, RISTA_IFRAME_FEATURES_IDS, LOCALSTORAGE_KEYS } from "utils/constants";
import { SERVICE_ROUTE } from "./utils/routeConstants";
import RedirectPage from "components/RedirectPage";
import { isOffline } from "facade/ServiceFacade";
import { handleOfflineError } from "helper/OfflineHelper";
import { getLocalStorage, getParsedLocalStorage, setLocalStorage } from "utils";
import Loader from "Containers/NewOrder/Loader";
import HomeLayout from "commonComponents/Layout/homeLayout";
import { sendPageViewEvent } from "helper/ga-helper";
import history from "./history";
import ReactGA from "react-ga4";
import { useSelector, useDispatch } from "react-redux";
import { saveLocalPrintersLocally, setDefaultPrinter } from "redux-store/slices/PrinterSlice";
import { lazyWithHandler } from "commonComponents/LazyLoadWrapper";
import { isEmpty } from "lodash";
import { FALLBACK_MIGRATION_COMPLETED, MIGRATION_COMPLETED, LOST_DATA_DEVICE, OLD_STATE_KEY } from "utils/migrateState";
import { exec, isAndroid } from "utils/nativeHooks";
import { useCustomerDisplayHost } from "hooks/useCustomerDisplayHost";
import { NW_APP_EVENT_KEYS } from "data/constants";

const LoginLazyLoading = lazyWithHandler(() => import('./Containers/Login/index'));
const TableViewContainerLazyLoading = lazyWithHandler(() => import('./Containers/TableView/index'));
const ReservationsContainerLazyLoading = lazyWithHandler(() => import('./Containers/Reservations/index'));
const MyOrderContainerLazyLoading = lazyWithHandler(() => import('./Containers/MyOrder/index'));
const IframeLazyLoading = lazyWithHandler(() => import('./Containers/Analytics/index'));
const DiscountLazyLoading = lazyWithHandler(() => import('./commonComponents/DiscountDealsWrapper/index'));
const CheckoutContainerLazyLoading = lazyWithHandler(() => import('./commonComponents/CheckoutContainerWrapper/index'));
const DeliveryDineInLazyLoading = lazyWithHandler(() => import('./commonComponents/DeliveryDineInModalWrapper/index'));
const ItemStatusLazyLoading = lazyWithHandler(() => import('./components/ItemStatusWrapper/index'));
const DetailViewLazyLoading = lazyWithHandler(() => import('./commonComponents/DetailsViewWrapper/index'));
const KioskLazyLoading = lazyWithHandler(() => import('./Containers/Kiosk/index'));
const CompaniesContainerLazyLoading = lazyWithHandler(() => import('./Containers/Companies/index'));

// Kiosk entry is gated OFF for release while the flow is incomplete — see
// @rista/core/kiosk/featureFlag, which is the whole switch. With it off the /kiosk route
// below does not exist AND the one-way door is a passthrough; both halves must go
// together, or a device with kiosk mode already persisted would redirect-loop between
// KioskLock and the catch-all route.
//
// ⚠️ This pairing is still load-bearing, because `KioskLock` reads
// `kioskConfigStore.enabled` STRAIGHT off the state rather than through
// `isKioskModeEnabled()` — it needs a reactive read. The flag-aware predicate covers the
// door's other half (history.js's push/replace guard, handleLogoutAction, apiService),
// which is what stops THOSE from going live on a build with no kiosk behind them.
const KioskGate = KIOSK_ENTRY_ENABLED ? KioskLock : Fragment;

const renderLazyComponent = (Component, otherData) => {
  return (
    <Suspense fallback={<Loader />}>
      <Component {...otherData} />
    </Suspense>
  )
}



export default function AppRoutes() {
  const userData = useSelector((state) => state?.businessStore?.userData);
  const localPrinters = useSelector((state) => state?.printerStore?.localPrinters);
  const defaultPrinterRedux = useSelector((state) => state?.printerStore?.defaultPrinter);
  const defaultPrinterOldRedux = useSelector((state) => state?.printerStore?.defaultPrinterDetails);
  const dispatch = useDispatch();

  // Confirms to the Electron shell that React mounted successfully. If this
  // never fires, the shell treats the OTA build as broken and auto-rolls back
  // to the bundled build. No-op outside Electron.
  useEffect(() => {
    window.electronAPI?.notifyReady?.();
  }, []);

   // TODO: to be removed once the default printer issue is fixed.
  useEffect(() => { // Pick default printer from localStorage and set in redux store on app load
    const oldStateStr = localStorage.getItem(OLD_STATE_KEY);
    try {
      const captureObj = {
        defaultPrinterLS: getLocalStorage(LOCALSTORAGE_KEYS.defaultPrinter) || "{}",
        defaultPrinterRedux: JSON.stringify(defaultPrinterRedux || {}),
        defaultPrinterOldRedux: JSON.stringify(defaultPrinterOldRedux || {}),
        localPrinterIds: (localPrinters || []).map((p) => p?.printerId || p?.mid || "").filter(Boolean).join(",") || "none",
        migrationCompleted: getLocalStorage(MIGRATION_COMPLETED),
        fallbackMigrationCompleted: getLocalStorage(FALLBACK_MIGRATION_COMPLETED),
        lostDataDevice: getLocalStorage(LOST_DATA_DEVICE),
        oldStateExists: !!oldStateStr,
        type: "fallbackMigrationAttempt",
      }
    } catch (error) {
      console.error("Error capturing lost printer issue:", error);
    }

    if (getLocalStorage(FALLBACK_MIGRATION_COMPLETED) || oldStateStr) {
      return;
    }
    const savedPrinter = getParsedLocalStorage(LOCALSTORAGE_KEYS.defaultPrinter);
    if (!isEmpty(savedPrinter)) {
      dispatch(setDefaultPrinter(savedPrinter));
      const printerId = savedPrinter?.printerId || savedPrinter?.mid || "";
      if (
        savedPrinter.type === "LOCAL" ||
        savedPrinter.printerType === "LOCAL" ||
        printerId.startsWith("local-printer_")
      ) {
        // retain local printer
        let updatedLocalPrinters;
        if (localPrinters?.length) {
          updatedLocalPrinters = [
            ...localPrinters.filter((p) => (p?.printerId || p?.mid) !== printerId),
            savedPrinter,
          ];
        } else {
          updatedLocalPrinters = [savedPrinter];
        }

        dispatch(saveLocalPrintersLocally(updatedLocalPrinters));
        console.log(
          "Migrated default printer to redux store and local printers list",
          savedPrinter,
        );
      }
    }
    setLocalStorage(FALLBACK_MIGRATION_COMPLETED, "true");
  }, []);

  useEffect(() => {
    // ✅ Safe cross-origin error catch
    try {
      parent.addEventListener("_platform_message", receiveDataFromNW);
    } catch (error) {
      console.warn('[Cross-Origin Warning]', error?.message);
      if (error && error?.toString().indexOf("parent") && isOffline()) {
        handleOfflineError("OFFLINE_DB_NOT_REACHABLE");
      }
    }
    return () => {
      // ✅ Safe cross-origin error catch
      try {
        parent.removeEventListener("_platform_message", receiveDataFromNW);
      } catch (error) {
        console.warn('[Cross-Origin Warning]', error?.message);
      }
    };
  }, []);

  // Opens the second-monitor customer display and serves it for as long as the
  // POS window is up.
  useCustomerDisplayHost();

  const isDeviceAndroid = isAndroid();
  useEffect(() => {
    if (isDeviceAndroid) {
      document.body.classList.add("is-android");
    }
  }, [isDeviceAndroid]);

  useEffect(() => {
    if (userData?.businessUserId) {
      ReactGA.set({
        user_id: userData?.userId
          ? userData?.businessUserId
          : getLocalStorage(LOCALSTORAGE_KEYS.deviceIdentifier) ||
            userData?.businessUserId,
      });
    } else {
      ReactGA.set({ user_id: undefined });
    }
  }, [userData?.businessUserId]);

  let unlistener = useRef(null);

  useEffect(() => {
    unlistener.current = history.listen((location) => {
      sendPageViewEvent({
        page: location.pathname + location.search,
        page_path: location.location.pathname,
        page_title: window.document.title,
        page_url: window.location.href,
      });
    });

    return () => {
      unlistener.current();
    };
  }, []);

  useEffect(() => {
    if (!window.electronAPI?.isElectron) {
      setLocalStorage(LOCALSTORAGE_KEYS.APP_VERSION, "");
      // default version is 1.0.0.2, which is currently used by merchants as on Mar 12, 2024
      setLocalStorage(LOCALSTORAGE_KEYS.nwAppVersion, "");
      exec(NW_APP_EVENT_KEYS.appversionnew, "", []);

      console.log("---INSIDE REACT--app.version-----sent");
      exec(NW_APP_EVENT_KEYS.appversion, "", []);
    }
  }, []);

  useEffect(() => {
    if (!window.electronAPI?.isElectron) return;
    // Listen for OTA "ready to restart" notifications on the unified channel.
    // Payload carries { track, version } — filter for OTA-track events.
    const ref = window.electronAPI.updates.onUpdateDownloaded((payload) => {
      if (payload?.track !== 'ota') return;
      toast.info(
        <div>
          <strong>Update v{payload.version} is ready!</strong>
          <div>Restart the app to apply the latest update.</div>
          <button
            style={{ marginTop: 8, padding: '4px 12px', cursor: 'pointer' }}
            onClick={() => window.electronAPI.updates.ota.restartToUpdate()}
          >
            Restart Now
          </button>
        </div>,
        { autoClose: false, closeOnClick: false, draggable: false }
      );
    });
    return () => window.electronAPI.updates.off('update-downloaded', ref);
  }, []);

  useEffect(() => {
    if (!window.electronAPI?.isElectron) return;
    // Shell (installer-level) update ready to apply. The install is per-user
    // (nsis.perMachine:false), so quitAndInstall runs unelevated and silent —
    // no UAC prompt, no NSIS wizard. The user just sees the app restart.
    const ref = window.electronAPI.updates.onUpdateDownloaded((payload) => {
      if (payload?.track !== 'shell') return;
      toast.info(
        <div>
          <strong>Update v{payload.version} is ready!</strong>
          <div>Rista Sales needs to restart to finish updating.</div>
          <button
            style={{ marginTop: 8, padding: '4px 12px', cursor: 'pointer' }}
            onClick={() => window.electronAPI.updates.shell.applyUpdate()}
          >
            Restart Now
          </button>
        </div>,
        { autoClose: false, closeOnClick: false, draggable: false }
      );
    });
    return () => window.electronAPI.updates.off('update-downloaded', ref);
  }, []);

  return (
    <KioskGate>
    <Routes>
      <Route path={LOGIN_ROUTE} element={renderLazyComponent(LoginLazyLoading)} />
      {/* All routes wrapped with HomeLayout */}
      <Route element={<HomeLayout />}>
        <Route path={ROUTE_CONSTANTS_MAP.NEW_ORDER_ROUTE} element={null} />
        <Route path={ROUTE_CONSTANTS_MAP.TABLE_VIEW_ROUTE} element={renderLazyComponent(TableViewContainerLazyLoading)} />
        <Route path={ROUTE_CONSTANTS_MAP.MY_ORDER_ROUTE} element={renderLazyComponent(MyOrderContainerLazyLoading)} />
        <Route path={ROUTE_CONSTANTS_MAP.RESERVATIONS_ROUTE} element={renderLazyComponent(ReservationsContainerLazyLoading)} />

        <Route path={ANALYTICS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.dashboard })} />
        <Route path={ACCESS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.access })} />
        <Route path={INVENTORY_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.inventory })} />
        <Route path={ACCOUNT_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.account })} />
        <Route path={SUPPORT_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.support })} />
        <Route path={SERVICE_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.service })} />
        <Route path={MARKETING_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.marketing })} />
        <Route path={REPORTS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.reports })} />
        <Route path={CATALOG_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.catalog })} />
        <Route path={ROUTE_CONSTANTS_MAP.SETTINGS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_MODULE_IDS.settings })} />
        <Route path={CUSTOMER_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: RISTA_IFRAME_FEATURES_IDS.customers })} />
        <Route path={MANAGE_TABLE_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.manageTable })} />
        <Route path={CASH_MANAGEMENT_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.cashManagement })} />
        <Route path={ROUTE_CONSTANTS_MAP.PNL_STATEMENT} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.pnlStatement })} />
        <Route path={ROUTE_CONSTANTS_MAP.SALES_AUDIT} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.salesAudit })} />
        <Route path={ROUTE_CONSTANTS_MAP.ACCOUNT_SUMMARY} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.accountSummary })} />
        <Route path={CASHIER_SETTLEMENT_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.cashierSettlement })} />
        <Route path={ROUTE_CONSTANTS_MAP.FINANCE_INVENTORY_MODULE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.financeInventoryModule })} />
        <Route path={OUTLET_STATUS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.outletStatus })} />
        
        <Route path={CASH_MANAGEMENT_ROUTE + "/:transactiontype"} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.cashManagement })} />
        <Route exact path={DISCOUNT_DEALS_ROUTE} element={renderLazyComponent(DiscountLazyLoading)} />
        <Route exact path={CUSTOMER_INFO_ROUTE} element={renderLazyComponent(DeliveryDineInLazyLoading)} />
        <Route exact path={DETAILS_VIEW_ROUTE} element={renderLazyComponent(DetailViewLazyLoading)} />

        <Route exact path={RISTA_CARDS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.ristaCards })} />
         <Route exact path={COMPANIES_ROUTE} element={renderLazyComponent(CompaniesContainerLazyLoading)} />        
        <Route exact path={CUSTOMER_DISPLAY_SETTINGS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.customerDisplay })} />
      </Route>

      <Route path={SUPPORT_LOGIN_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.supportLogin, renderOnlyIframe: true })} />
      <Route path={ROUTE_CONSTANTS_MAP.CHANGE_BUSINESS_ROUTE} element={renderLazyComponent(IframeLazyLoading, { pageId: MERCHANT_PANEL_PAGE_IDS.changeBusiness, renderOnlyIframe: true })} />

      {/* Kiosk is customer-facing: outside HomeLayout so no staff chrome renders. */}
      {/* `/*` so the kiosk's own child routes (service, catalog, cart, …) resolve. */}
      {KIOSK_ENTRY_ENABLED && (
        <Route path={`${ROUTE_CONSTANTS_MAP.KIOSK_ROUTE}/*`} element={renderLazyComponent(KioskLazyLoading)} />
      )}

      <Route exact path={REDIRECT_PAGE_ROUTE} element={<RedirectPage />} />
      <Route exact path={CHECKOUT_CONTAINER_ROUTE} element={renderLazyComponent(CheckoutContainerLazyLoading)} />
      <Route exact path={ITEM_STATUS_ROUTE} element={renderLazyComponent(ItemStatusLazyLoading)} />

      <Route path="*" element={<Navigate to={LOGIN_ROUTE} />} />
    </Routes>
    </KioskGate>
  );
}

const receiveDataFromNW = (e) => {
  const data = e.detail,
    key = data.key,
    message = data.message;
  if (key === "app.version") {
    const nwPlatform = message.platform === 'darwin' ? 'mac' : 'win';
    setLocalStorage(LOCALSTORAGE_KEYS.nwPlatform, nwPlatform);
    setLocalStorage(LOCALSTORAGE_KEYS.isShowOfflineModeToggle, true);
  } else if (key === "app.version.new") {
    setLocalStorage(LOCALSTORAGE_KEYS.nwAppVersion, message.version || "");
  }
};
